import { useEffect, useRef, useState } from 'react';
import { connect } from 'redux-bundler-react';
import { useFormContext } from 'react-hook-form';
import { Button, Label, TextInput as UswdsTextInput } from '@trussworks/react-uswds';
import classNames from 'classnames';

import Tooltip from '../tooltip/tooltip';
import FilePreview from './FilePreview';
import FileDownloadList from '../file-download-list/FileDownloadList';

import { filenameRegex } from '@src/utils/regex';
import { ErrorMessages, StorageReasons } from '@src/utils/enums';

import './fileinput.scss';
import './filePreview.scss';

const FileInput = connect(
  'selectCombinedRequestFiles',
  'selectAirFiles',
  'selectMaxFileCount',
  'selectMaxIndFileSize',
  'selectMaxTotalFileSize',
  'selectSelectedFiles',
  'selectTotalFileSize',
  ({
    accept,
    arHeader = false,
    chooseText,
    className = 'width-full',
    combinedRequestFiles,
    airFiles,
    deleteFileCallback = () => {},
    downloadFileCallback = () => {},
    dragText,
    fileDownload,
    hint,
    id,
    label,
    maxFileCount,
    maxIndFileSize,
    maxTotalFileSize,
    multiple = false,
    name,
    onChangeCallback = () => {},
    readOnly,
    required,
    resetFileErrors = false,
    scope,
    selectedFiles,
    setFileErrors = () => {},
    showDownloadButton = false,
    showDeleteButton = false,
    showOptionalText = true,
    tooltip,
    tooltipClickable,
    totalFileSize,
    validateOnPickerClose = false,
    pickerCloseErrorMessage = ErrorMessages.GDBFileMissing,
    value,
    ...customProps
  }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorText, setErrorText] = useState([]);
    const [files, setFiles] = useState([]);
    const pickerFocusHandlerRef = useRef(null);
    const pickerValidationTimeoutRef = useRef(null);
    const previewHeaderText = files?.length > 1 ? `${files.length} files selected` : 'Selected file';

    const isSingleFile = files?.length <= 1;

    const defaultText = {
      dragText: `Drag file${multiple ? 's' : ''} here or `,
      chooseText: 'choose from folder',
      errorText: 'This is not a valid file type.',
    };

    const {
      register,
      setError,
      clearErrors,
      formState: { errors },
    } = useFormContext();

    const inputError = errors[name];

    const isFileInvalid = (!resetFileErrors && showError) || inputError;

    const fileInputStyles = classNames(
      'usa-file-input',
      {
        'usa-file-input--disabled': readOnly,
      },
      className
    );

    const targetStyles = classNames('usa-file-input__target', {
      'usa-file-input--drag': isDragging,
      'has-invalid-file': isFileInvalid,
    });

    const instructionStyles = classNames('usa-file-input__instructions', {
      'display-none': files?.length > 0,
      'file-invalid': isFileInvalid,
    });

    const labelStyles = classNames('usa-label', {
      required: required,
      optional: !required && showOptionalText,
    });

    const fileInputPreviewHeadingClasses = classNames('usa-file-input__preview-heading', {
      'file-input-disabled': readOnly,
      'file-input-invalid': isFileInvalid,
    });

    const fileInputBoxClasses = classNames('usa-file-input__box', {
      'file-input-disabled': readOnly,
    });

    const removePickerFocusHandler = () => {
      if (pickerFocusHandlerRef.current) {
        window.removeEventListener('focus', pickerFocusHandlerRef.current);
        pickerFocusHandlerRef.current = null;
      }

      if (pickerValidationTimeoutRef.current) {
        window.clearTimeout(pickerValidationTimeoutRef.current);
        pickerValidationTimeoutRef.current = null;
      }
    };

    const handleFilePickerOpen = () => {
      if (!validateOnPickerClose || readOnly) {
        return;
      }
      clearErrors(name);
      removePickerFocusHandler();
      const handleWindowFocus = () => {
        pickerValidationTimeoutRef.current = window.setTimeout(() => {
          const fileInput = document.getElementById(id);

          if (!fileInput?.files?.length) {
            setError(name, {
              type: 'manual',
              message: pickerCloseErrorMessage,
            });
          }

          removePickerFocusHandler();
        }, 300);
      };
      pickerFocusHandlerRef.current = handleWindowFocus;
      window.addEventListener('focus', handleWindowFocus);
    };

    const preventInvalidFiles = (e) => {
      setShowError(false);
      setFileErrors(false);
      // Data transfer or target attributes depends on if event is an onChange or onDrop
      const stagedFiles = e?.dataTransfer?.files ? Array.from(e?.dataTransfer?.files) : Array.from(e?.target?.files);
      const stagedFileSizesArr = stagedFiles.length > 0 ? stagedFiles?.map((file) => file.size) : [];
      const stagedFileSizesArrSum = stagedFileSizesArr.reduce(
        (accumulator, currentValue) => accumulator + currentValue,
        0
      );
      let errorsArr = [];

      if (stagedFiles.length > 0) {
        stagedFiles.forEach((file) => {
          // Parse out file extension in the filename
          const filename = file?.name?.replace(/\.[^/.]+$/, '');

          // File does not have an accepted file extension
          if (accept) {
            const fileType = `.${file?.name?.split('.').pop()}`;
            const acceptedTypes = accept.split(',');
            !acceptedTypes.includes(fileType) && errorsArr.push(`${file?.name}: ${defaultText.errorText}`);
          }

          // File is empty
          if (file?.size === 0) {
            errorsArr.push(`${file?.name}: ${ErrorMessages.FileEmpty}`);
          }

          // Exceeds max individual file size (binary bytes) 100MB
          if (file?.size > maxIndFileSize) {
            errorsArr.push(`${file?.name}: ${ErrorMessages.FileTooLarge}`);
          }

          // File already exists in S3 or has been staged already
          if (scope === StorageReasons.AirRequest) {
            if (airFiles?.filter((item) => item.fileName === file.name).length > 0) {
              errorsArr.push(`${file?.name}: ${ErrorMessages.FileExists}`);
            }
          } else {
            if (combinedRequestFiles?.filter((item) => item.fileName === file.name).length > 0) {
              errorsArr.push(`${file?.name}: ${ErrorMessages.FileExists}`);
            }
          }

          // Exceeds max character length (1024 bytes)
          if (filename.length > 1024) {
            errorsArr.push(`${file?.name}: ${ErrorMessages.FileNameTooLong}`);
          }

          // Filename contains invalid characters per S3 naming convention
          if (!filename.match(filenameRegex)) {
            errorsArr.push(`${file?.name}: ${ErrorMessages.FileNameInvalidCharacters}`);
          }

          // Exceeds total file limit (500MB)
          if ((isNaN(totalFileSize) ? 0 : totalFileSize) + stagedFileSizesArrSum > maxTotalFileSize) {
            errorsArr.push(`${file?.name}: ${ErrorMessages.OverTotalFileLimit}`);
          }

          // Exceeds file count limit (50 files)
          if (selectedFiles.length + stagedFiles.length > maxFileCount) {
            errorsArr.push(`${file?.name}: ${ErrorMessages.OverTotalFileCount}`);
          }
        });
      }

      if (errorsArr.length > 0) {
        setShowError(true);
        setFileErrors(true);
        setErrorText(errorsArr);
        e.preventDefault();
        e.stopPropagation();
        return true;
      }
      return false;
    };

    const handleChange = (e) => {
      removePickerFocusHandler(); // determine if picker returned with a selected file
      if (e.target.files && e.target.files.length > 0) {
        const filesArr = Array.from(e?.target?.files);
        // Check if staged file(s) is a duplicate
        const isDuplicateArr =
          files?.map((item) => filesArr.filter((item2) => item2.name === item.fileName).length > 0) ?? [];
        // If any of the staged file(s) is a duplicate, do not validate
        if (isDuplicateArr && !isDuplicateArr?.some((x) => x === true)) {
          const invalidFilesExist = preventInvalidFiles(e);
          !invalidFilesExist && onChangeCallback(e);
        }
      }
    };

    const handleClearFile = () => {
      setFiles([]);
      setShowError(false);
      setFileErrors(false);
      setErrorText([]);
      //Clear the staged file in the browser FileList
    };

    const handleDeleteFile = () => {
      handleClearFile();
      deleteFileCallback();
    };

    const handleDownloadFile = () => {
      downloadFileCallback();
    };

    const handleDragOver = () => setIsDragging(true);
    const handleDragLeave = () => setIsDragging(false);
    const handleDrop = () => setIsDragging(false);

    useEffect(() => {
      setFiles(value);
    }, [value]);

    useEffect(() => {
      return () => {
        removePickerFocusHandler();
      };
    }, []);

    const {
      ref: fileInputRef,
      onBlur: registeredOnBlur,
      ...rest
    } = register(name, {
      onChange: handleChange,
    });

    return (
      <>
        {label && (
          <Label className={labelStyles}>
            <span id={`${name}_label`}>{label}</span>
            {tooltip && <Tooltip clickable={tooltipClickable} content={tooltip} header={label} name={name} />}
          </Label>
        )}
        {hint && (
          <div className='usa-hint' id={`${name}_hint`}>
            {hint}
          </div>
        )}
        {fileDownload?.length > 0 && <FileDownloadList files={fileDownload} />}
        <div className={fileInputStyles} aria-disabled={readOnly}>
          <div className={targetStyles} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
            {files?.length > 0 && (
              <div className={fileInputPreviewHeadingClasses}>
                {previewHeaderText}{' '}
                {!readOnly && <span className='usa-file-input__choose'>Change file{files.length > 1 && 's'}</span>}
              </div>
            )}
            <div className={instructionStyles} aria-hidden='true'>
              <span className='usa-file-input__drag-text'> {dragText || defaultText.dragText} </span>
              <span className='usa-file-input__choose'> {chooseText || defaultText.chooseText} </span>
            </div>
            {/* Displays File Preview list */}
            {files?.map((file, index) => (
              <FilePreview
                key={`filePreview_${name}_${index}`}
                imageId={`${name}_${index}`}
                file={file}
                readOnly={readOnly}
                showError={showError}
                resetFileErrors={resetFileErrors}
              />
            ))}
            <div className={fileInputBoxClasses} />
            {/* Displays File Errors list */}
            {showError === true && resetFileErrors === false && (
              <>
                {errorText?.length > 0 &&
                  errorText.map((text, index) => (
                    <div key={index} className='usa-file-input__accepted-files-message margin-top-1'>
                      {text}
                    </div>
                  ))}
              </>
            )}
            <UswdsTextInput
              accept={accept}
              className='usa-file-input usa-file-input__input'
              id={id}
              multiple={multiple}
              name={name}
              disabled={readOnly}
              inputRef={fileInputRef}
              required={required}
              type='file'
              validationStatus={inputError && 'error'}
              aria-describedby={hint ? `${name}_hint` : undefined}
              {...customProps}
              {...rest}
              onBlur={validateOnPickerClose ? undefined : registeredOnBlur}
              onClick={handleFilePickerOpen}
            />
          </div>
        </div>
        <div className='display-flex flex-justify-end margin-top-1'>
          {showDownloadButton && (
            <Button
              className='button-small'
              disabled={files?.length === 0}
              outline={files?.length === 0}
              onClick={handleDownloadFile}
            >
              Download{isSingleFile ? '' : ' All'}
            </Button>
          )}
          {!arHeader && (!readOnly || showDeleteButton) && (
            <Button
              className='button-small'
              disabled={files?.length === 0 || (readOnly && !showDeleteButton)}
              onClick={handleDeleteFile}
              outline={files?.length === 0}
              secondary={files?.length >= 1}
            >
              {`Remove${isSingleFile ? '' : ' All'}`}
            </Button>
          )}
        </div>
      </>
    );
  }
);

export default FileInput;
